import { Component, OnInit, inject } from '@angular/core';
import { Validators, FormGroup, ReactiveFormsModule, NonNullableFormBuilder } from '@angular/forms';
import { FormsService, FormValidator } from '@app/core/services/forms.service';
import { WaitIndicatorService } from '@app/core/services/wait-indicator.service';
import { RecaptchaService } from '@app/core/services/recaptcha.service';
import { AccountService } from '@api';
import { switchMap, shareReplay, map, catchError, take } from 'rxjs/operators';
import { of } from 'rxjs';
import { AlertService } from '@app/core/services/alert.service';
import { ControlsOf } from '@app/core/functions/controls-of';
import { WtDateInputComponent } from '@app/core/components/wt-date-input.component';
import { NumericOnlyDirective } from '@app/core/directives/numericOnly.directive';
import { WtFocusableDirective } from '@app/core/directives/wt-focusable.directive';
import { WtAutofocusDirective } from '@app/core/directives/wt-autofocus.directive';
import { WtValidationStateDirective } from '@app/core/directives/wt-validation-state.directive';
import { AsyncPipe } from '@angular/common';
import { WtValidationMessagesComponent, WtValidationMessageComponent } from '@app/core/components/wt-validation-errors.component';
import { UsernameRecoveryService } from '../services/username-recovery.service';

@Component({
  selector: 'app-recover-username',
  template: `<h2 class="title">Recover Username</h2>

    @if (recoverComplete) {
      <div>
        <div class="alert alert-success" role="alert">
          <span class="glyphicon glyphicon-ok-sign" aria-hidden="true"></span>
          <span class="sr-only">Success:</span>
          Your username has been emailed to you at {{ recoverComplete }}. If you do not receive it, please wait a few minutes and check your
          spam folder.
        </div>
        <p><a class="btn btn-primary" href="/loanmanager/login/">Click here to login</a></p>
      </div>
    }

    @if (!recoverComplete) {
      <div>
        <p>Please fill out the following form to recover your username.</p>
        <form [formGroup]="form" class="form-horizontal col-sm-8" novalidate>
          <div class="row form-group required" [wtValidationState]="validator.getControlStatus('loanno') | async">
            <label class="col-md-3 control-label" for="id_loanno"> Loan Number </label>
            <div class="col-md-9">
              <input
                formControlName="loanno"
                class="form-control"
                id="id_loanno"
                maxlength="10"
                name="loanno"
                placeholder="Loan Number"
                required="required"
                type="text"
                wtAutofocus
                wtFocusable
              />
              <wt-validation-messages controlName="loanno" [validator]="validator">
                <wt-validation-message name="required">This field is required</wt-validation-message>
                <wt-validation-message name="minlength">Must be 10 digits</wt-validation-message>
                <wt-validation-message name="maxlength">Must be 10 digits</wt-validation-message>
              </wt-validation-messages>
            </div>
          </div>
          <div class="row form-group required" [wtValidationState]="validator.getControlStatus('ssn') | async">
            <label class="col-md-3 control-label" for="id_ssn"> Social Security Number </label>
            <div class="col-md-9">
              <input
                formControlName="ssn"
                class="form-control"
                id="id_ssn"
                minlength="9"
                maxlength="9"
                name="ssn"
                placeholder="Please enter only digits with no hyphens or spaces"
                required="required"
                type="password"
                appNumericOnly
                wtFocusable
              />
              <wt-validation-messages controlName="ssn" [validator]="validator">
                <wt-validation-message name="required">This field is required</wt-validation-message>
                <wt-validation-message name="minlength">Must be 9 digits</wt-validation-message>
                <wt-validation-message name="maxlength">Must be 9 digits</wt-validation-message>
              </wt-validation-messages>
            </div>
          </div>
          <div class="row form-group required" [wtValidationState]="validator.getControlStatus('dob') | async">
            <label class="col-md-3 control-label" for="id_dob"> Date of Birth </label>
            <div class="col-md-9">
              <wt-date-input formControlName="dob" />
              <wt-validation-messages controlName="dob" [validator]="validator">
                <wt-validation-message name="required">This field is required</wt-validation-message>
              </wt-validation-messages>
            </div>
          </div>
          <div class="row form-group">
            <div class="col-md-offset-3 col-md-9">
              <button class="btn btn-primary" (click)="recoverUsername()">Continue</button>
            </div>
          </div>
        </form>
      </div>
    }`,
  imports: [
    ReactiveFormsModule,
    WtValidationStateDirective,
    WtAutofocusDirective,
    WtFocusableDirective,
    WtValidationMessagesComponent,
    WtValidationMessageComponent,
    NumericOnlyDirective,
    WtDateInputComponent,
    AsyncPipe,
  ],
})
export class RecoverUsernameComponent implements OnInit {
  private fb = inject(NonNullableFormBuilder);
  private formsService = inject(FormsService);
  private waitIndicatorService = inject(WaitIndicatorService);
  private recaptchaService = inject(RecaptchaService);
  private accountService = inject(AccountService);
  private alertService = inject(AlertService);
  private usernameRecoveryService = inject(UsernameRecoveryService);

  public form: FormGroup<
    ControlsOf<{
      loanno: string;
      ssn: string;
      dob: string;
    }>
  >;
  public validator: FormValidator;
  public recoverComplete = '';

  ngOnInit() {
    const state = this.usernameRecoveryService.getCache();

    this.form = this.fb.group({
      loanno: [state ? state.loanno : '', [Validators.required, Validators.minLength(10), Validators.maxLength(10)]],
      ssn: [state ? state.ssn : '', [Validators.required, Validators.minLength(9)]],
      dob: [state ? state.dob : '', Validators.required],
    });

    this.validator = this.formsService.getFormValidator(this.form);

    if (state) {
      this.recoverUsername();
      this.usernameRecoveryService.clearCache();
    }
  }

  recoverUsername() {
    this.validator.setSubmitted();

    if (!this.form.valid) {
      return of(null);
    }

    this.waitIndicatorService.setWaiting();

    const response = this.recaptchaService
      .getNewRecaptchaToken('recover_username')
      .pipe(
        switchMap((recaptcha_response) =>
          this.accountService
            .accountRecoverUsernameCreate({
              recoverUsername: {
                loanno: this.form.getRawValue().loanno,
                ssn: this.form.getRawValue().ssn,
                dob: this.form.getRawValue().dob,
                recaptcha_response: recaptcha_response,
              },
            })
            .pipe(
              map((result) => (this.recoverComplete = result.email_address)),
              catchError(() => {
                this.alertService.alert('There was a problem recovering your username.');
                return of(undefined);
              })
            )
        ),
        map(() => {
          this.waitIndicatorService.setReady();
        }),
        shareReplay()
      )
      .pipe(take(1));

    response.subscribe();

    return response;
  }
}
